import { ImgID } from "../../image_loader/img_id.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { UIGradientOverlay } from "../common_ui_elements/ui_gradient_overlay.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { RoundedIconRectButton } from "../common_ui_elements/buttons/rounded_outline_rect_button.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogId } from "./dialog_id.js";
import { DialogManager } from "./dialog_manager.js";
import { AppConfigService } from "../../app_config/app_config_service.js";
import { audioController } from "../../audio/audio_controller.js";
import { AudioClips } from "../../audio/audio_constants.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";

export enum AppVersionStatus
{
  Ok   = 'ok',
  Hard = 'hard',
  Soft = 'soft',
}

export enum UpdateRequiredMode
{
  Hard = 'hard',
  Soft = 'soft',
}

export interface IVersionCheckResult
{
  status: AppVersionStatus;
  soft_version: number;
}

export class UpdateRequiredDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.UPDATE_REQUIRED_DIALOG;

  private static readonly STORAGE_KEY = 'update_soft_dismissed';
  private static readonly DIALOG_W = 909;
  private static readonly DIALOG_H = 842;

  private static hard_open_count = 0;
  public static on_hard_closed: ( () => void ) | null = null;

  public static isHardOpen(): boolean
  {
    return UpdateRequiredDialog.hard_open_count > 0;
  }

  public static isSoftDismissed( soft_version: number ): boolean
  {
    return parseInt( localStorage.getItem( UpdateRequiredDialog.STORAGE_KEY ) ?? '0' ) >= soft_version;
  }

  private static dismissSoft( soft_version: number ): void
  {
    localStorage.setItem( UpdateRequiredDialog.STORAGE_KEY, String( soft_version ) );
  }

  private bg!: UINineSliceElement;
  private mode: UpdateRequiredMode;
  private soft_version: number;

  constructor( mode: UpdateRequiredMode, soft_version = 0 )
  {
    const body_w = LayoutScaler.widthFromDesign( UpdateRequiredDialog.DIALOG_W );
    const body_h = LayoutScaler.heightFromDesign( UpdateRequiredDialog.DIALOG_H );
    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - body_h ) / 2;

    super( DialogManager.ctx, body_x, body_y, body_w, body_h, mode !== UpdateRequiredMode.Hard );

    this.mode = mode;
    this.soft_version = soft_version;

    if ( mode === UpdateRequiredMode.Hard )
      UpdateRequiredDialog.hard_open_count++;

    this.createBackground();
    this.createTitle();
    this.createText();
    this.createButton();
  }

  public name(): string { return "UpdateRequiredDialog"; }

  public isHardBlocking(): boolean
  {
    return this.mode === UpdateRequiredMode.Hard;
  }

  public override onDialogWillClose(): void
  {
    if ( this.mode !== UpdateRequiredMode.Hard )
      return;

    UpdateRequiredDialog.hard_open_count = Math.max( 0, UpdateRequiredDialog.hard_open_count - 1 );
    UpdateRequiredDialog.on_hard_closed?.();
  }

  private openStore(): void
  {
    const store_url = AppConfigService.get().app_version.android.store_url;
    window.open( store_url, '_blank' );
  }

  private createBackground(): void
  {
    const w = this.rect.getW();
    const h = this.rect.getH();

    const bg_w           = this.rect.getW();
    const bg_h           = this.rect.getH();
    this.bg = new UINineSliceElement(
      this.ctx,
      0,
      0,
      bg_w,
      bg_h,
      ImgID.UI_CapsuleBlue,
      "capsule_bg"
    );

    this.addChild( this.bg );

    const icon_bg_size = LayoutScaler.widthFromDesign( 290 );
    const icon_bg_x = w / 2 - icon_bg_size / 2;
    const icon_bg_y = -icon_bg_size / 2 + LayoutScaler.yFromDesign( 22 );

    const bg_circle = new UIImage(
      this.ctx,
      icon_bg_x,
      icon_bg_y,
      icon_bg_size,
      icon_bg_size,
      ImgID.AvatarCircle,
      {},
      false
    );
    this.addChild( bg_circle );

    const icon_w = LayoutScaler.widthFromDesign( 210 );
    const icon_h = LayoutScaler.heightFromDesign( 210 );
    const icon_x = icon_bg_size / 2 - icon_w / 2;
    const icon_y = LayoutScaler.yFromDesign( 38 );

    const icon = new UIImage(
      this.ctx,
      icon_x,
      icon_y,
      icon_w,
      icon_h,
      ImgID.UI_RateGameIcon,
      {},
      false
    );
    bg_circle.addChild( icon );
  }

  private createTitle(): void
  {
    const title_h = LayoutScaler.heightFromDesign( 100 );
    const title_y = LayoutScaler.yFromDesign( 136 );

    const title = new UITextElement(
      this.ctx,
      0,
      title_y,
      this.bg.getRect().getW(),
      title_h,
      {
        localization_key: "UPDATE_REQUIRED.TITLE",
        font_size: LayoutScaler.textSizeFromDesign( 70 ),
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        font_weight: 900,
        outline_color: ColorScheme.menu_top_el_text,
        outline_width: LayoutScaler.widthFromDesign( 14 ),
      }
    );
    this.bg.addChild( title );
  }

  private createText(): void
  {
    const text_key = this.mode === UpdateRequiredMode.Hard
      ? 'UPDATE_REQUIRED.TEXT_HARD'
      : 'UPDATE_REQUIRED.TEXT_SOFT';

    const text_h = LayoutScaler.heightFromDesign( 250 );
    const text_y = LayoutScaler.yFromDesign( 300 );
    const text_x = this.bg.getRect().getW() * 0.05;
    const text_w = this.bg.getRect().getW() * 0.9;

    const text = new UITextElement(
      this.ctx,
      text_x,
      text_y,
      text_w,
      text_h,
      {
        localization_key: text_key,
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        font_weight: 900,
        vertical_align: MyTextVerticalAlign.Top,
        auto_wrap: true,
        outline_color: ColorScheme.menu_top_el_text,
        outline_width: LayoutScaler.widthFromDesign( 10 ),
      }
    );
    this.bg.addChild( text );
  }

  private createButton(): void
  {
    if ( this.mode === UpdateRequiredMode.Hard )
      this.createHardButton();
    else
      this.createSoftButtons();
  }

  private createHardButton(): void
  {
    const btn_w = LayoutScaler.widthFromDesign( 700 );
    const btn_h = LayoutScaler.heightFromDesign( 280 );
    const btn_x = this.bg.getRect().getW() / 2 - btn_w / 2;
    const btn_y = this.bg.getRect().getH() - btn_h - LayoutScaler.yFromDesign( 25 );

    const btn = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      undefined,
      true
    );

    btn.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      this.openStore();
    } );

    const text = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        localization_key: "UPDATE_REQUIRED.UPDATE",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        font_weight: 800,
        shadow_blur: LayoutScaler.widthFromDesign( 6 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
        outline_color: ColorScheme.menu_button_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      }
    );
    btn.addChild( text, true );
    this.bg.addChild( btn );
  }

  private createSoftButtons(): void
  {
    // Two 700 x 280 buttons do not fit side by side, so each keeps the art's
    // 700:280 proportions at a width that fits the row.
    const btn_w = LayoutScaler.widthFromDesign( 410 );
    const btn_h = LayoutScaler.heightFromDesign( 164 );
    const gap = LayoutScaler.widthFromDesign( 20 );
    const btn_y = this.bg.getRect().getH() - btn_h - LayoutScaler.yFromDesign( 78 );
    const start_x = ( this.bg.getRect().getW() - ( btn_w * 2 + gap ) ) / 2;

    const btn_later = new UIImage(
      this.ctx,
      start_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_RedBtn,
      undefined,
      true
    );
    btn_later.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      UpdateRequiredDialog.dismissSoft( this.soft_version );
      DialogManager.closeDialog();
    } );

    const text_later = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        localization_key: "UPDATE_REQUIRED.LATER",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        font_weight: 800,
        shadow_blur: LayoutScaler.widthFromDesign( 6 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
        outline_color: ColorScheme.red_btn_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      }
    );
    btn_later.addChild( text_later, true );

    const btn_update = new UIImage(
      this.ctx,
      start_x + btn_w + gap,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_GreenFrameBtn,
      undefined,
      true
    );
    btn_update.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      this.openStore();
    } );

    const text_update = new UITextElement(
      this.ctx, 0, 0, btn_w, btn_h,
      {
        localization_key: "UPDATE_REQUIRED.UPDATE",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        text_align: MyTextAlign.Center,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        font_weight: 800,
        shadow_blur: LayoutScaler.widthFromDesign( 6 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
        outline_color: ColorScheme.menu_button_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
      }
    );
    btn_update.addChild( text_update, true );

    this.bg.addChild( btn_later );
    this.bg.addChild( btn_update );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;
    this.startRender();
    this.finishRender();
  }
}
